import { useState, useRef, useCallback, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion, AnimatePresence, useScroll, useTransform } from "framer-motion";
import {
  Sparkles, ArrowRight, Phone, Printer, Zap, Globe,
  Lock, Brain, ShieldCheck, Wand2, ChevronDown, Type, Palette,
  Image as ImageIcon, X, CalendarDays, Building2, Database,
  Upload,
} from "lucide-react";
import { toast } from "sonner";
import { isProviderHost } from "@/lib/platformHosts";

import RevealNav from "@/components/RevealNav";
import SiteFooter from "@/components/SiteFooter";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import VoiceSamplePlayer from "@/components/VoiceSamplePlayer";
import { api, BUILD_SESSION } from "@/lib/api";

// ---------------------------------------------------------------------------
// Data
// ---------------------------------------------------------------------------
const STYLE_CHIPS = [
  { id: "warm_editorial", label: "Warm editorial", desc: "Cream, serif accents, gallery" },
  { id: "modern_minimal", label: "Modern minimal", desc: "White, grid, ample whitespace" },
  { id: "bold_street",    label: "Bold streetwear", desc: "Black, neon, oversized type" },
  { id: "luxury_dark",    label: "Luxury dark",     desc: "Onyx, gold, hushed luxury" },
];

const BUSINESS_TYPES = [
  { id: "restaurant", label: "Restaurant" },
  { id: "bakery", label: "Bakery" },
  { id: "retail",     label: "Retail" },
  { id: "other",      label: "Other" },
];

const CUISINE_TAGS = [
  "American", "Mexican", "Italian", "Japanese", "Chinese", "Thai",
  "Indian", "Mediterranean", "Korean", "Vietnamese", "French",
  "BBQ", "Pizza", "Burgers", "Seafood", "Vegan", "Bakery", "Cafe",
];

const SERVICE_CATEGORY_TAGS = [
  "Hair Salon", "Barbershop", "Nail Salon", "Spa", "Massage",
  "Skincare", "Med Spa", "Dental", "Chiropractic", "Physical Therapy",
  "Auto Repair", "Detailing", "Tattoo", "Fitness Studio", "Pet Grooming",
  "Photography", "Cleaning", "Tutoring",
];

const VIBE_TAGS = [
  { id: "cozy",       label: "Cozy" },
  { id: "upscale",    label: "Upscale" },
  { id: "playful",    label: "Playful" },
  { id: "rustic",     label: "Rustic" },
  { id: "industrial", label: "Industrial" },
  { id: "minimal",    label: "Minimal" },
  { id: "bold",       label: "Bold" },
  { id: "elegant",    label: "Elegant" },
  { id: "casual",     label: "Casual" },
  { id: "vintage",    label: "Vintage" },
  { id: "modern",     label: "Modern" },
  { id: "artsy",      label: "Artsy" },
];

// Named palettes — each is a full color story (primary + accent + bg feel).
// Selecting a palette sets primary_color; secondary derives from the story.
const COLOR_PALETTES = [
  {
    id: "sienna",
    name: "Burnt sienna",
    desc: "Warm editorial — terracotta warmth on cream paper",
    swatches: ["#C84B31", "#E07B39", "#F5E6D0", "#1A1208"],
    primary: "#C84B31",
    secondary: "#E07B39",
  },
  {
    id: "midnight",
    name: "Midnight ink",
    desc: "Dark luxury — near-black with gold accent",
    swatches: ["#0D1B2A", "#1A1A2E", "#C9A84C", "#F0EDE8"],
    primary: "#0D1B2A",
    secondary: "#C9A84C",
  },
  {
    id: "forest",
    name: "Forest & cream",
    desc: "Earthy organic — deep green with warm off-white",
    swatches: ["#2D4A2D", "#4A7C59", "#F2ECD8", "#8B6914"],
    primary: "#2D4A2D",
    secondary: "#4A7C59",
  },
  {
    id: "ocean",
    name: "Ocean slate",
    desc: "Cool modern — deep teal with soft blue-grey",
    swatches: ["#1B3A4B", "#2B5F8F", "#E8EFF5", "#3D7A8A"],
    primary: "#1B3A4B",
    secondary: "#2B5F8F",
  },
  {
    id: "rouge",
    name: "Deep rouge",
    desc: "Bold romantic — wine red with blush",
    swatches: ["#7B1D2B", "#A63248", "#F5DDE0", "#E8A0A8"],
    primary: "#7B1D2B",
    secondary: "#A63248",
  },
  {
    id: "obsidian",
    name: "Obsidian & lime",
    desc: "Street energy — black with sharp neon green",
    swatches: ["#111111", "#222222", "#A8FF3E", "#F5F5F5"],
    primary: "#111111",
    secondary: "#A8FF3E",
  },
  {
    id: "sand",
    name: "Sand & copper",
    desc: "Mediterranean warmth — sandy neutrals with copper",
    swatches: ["#B5854A", "#D4A96A", "#F5EDD8", "#8B5E2A"],
    primary: "#B5854A",
    secondary: "#D4A96A",
  },
  {
    id: "violet",
    name: "Violet dusk",
    desc: "Moody upscale — deep purple with lavender",
    swatches: ["#2D1B4E", "#6B5CA5", "#E8E0F5", "#9B89C4"],
    primary: "#2D1B4E",
    secondary: "#6B5CA5",
  },
];


// Font pairs — heading + body shown as rendered samples
const FONT_PAIRS = [
  {
    id: "playfair_inter",
    heading: "Playfair Display",
    body: "Inter",
    label: "Editorial",
    sample: "Fine Dining & Heritage",
    mood: "Refined, classic, restaurant-editorial",
  },
  {
    id: "bebas_nunito",
    heading: "Bebas Neue",
    body: "Nunito",
    label: "Bold street",
    sample: "STREET FOOD & FLAVOR",
    mood: "High energy, bold, streetwear",
  },
  {
    id: "cormorant_lato",
    heading: "Cormorant Garamond",
    body: "Lato",
    label: "Luxury",
    sample: "Atelier & Terroir",
    mood: "Quiet luxury, fine dining",
  },
  {
    id: "outfit_outfit",
    heading: "Outfit",
    body: "Outfit",
    label: "Modern",
    sample: "Clean & Contemporary",
    mood: "Modern minimal, tech-forward",
  },
  {
    id: "dm_serif_dm_sans",
    heading: "DM Serif Display",
    body: "DM Sans",
    label: "Warm modern",
    sample: "Comfort & Craft",
    mood: "Approachable, warm, contemporary",
  },
  {
    id: "space_grotesk_space",
    heading: "Space Grotesk",
    body: "Space Grotesk",
    label: "Sharp",
    sample: "Precision & Edge",
    mood: "Geometric, sharp, innovative",
  },
];

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
function isValidHex(h) {
  return /^#[0-9A-Fa-f]{6}$/.test(h);
}

function ScrollSection({ children, className = "", tone = "light", ...props }) {
  return (
    <motion.section
      className={`scroll-section ${tone === "dark" ? "scroll-section--dark" : ""} ${className}`}
      initial={{ opacity: 0.18, y: 96, scale: 0.985 }}
      whileInView={{ opacity: 1, y: 0, scale: 1 }}
      viewport={{ once: false, amount: 0.22, margin: "-10% 0px -10% 0px" }}
      transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
      {...props}
    >
      {children}
    </motion.section>
  );
}

function ScrollCard({ children, className = "", delay = 0 }) {
  return (
    <motion.div
      className={className}
      initial={{ opacity: 0, x: 64, y: 72, rotateX: 4, rotateZ: 1.5 }}
      whileInView={{ opacity: 1, x: 0, y: 0, rotateX: 0, rotateZ: 0 }}
      viewport={{ once: false, amount: 0.28 }}
      transition={{ duration: 0.72, delay, ease: [0.16, 1, 0.3, 1] }}
    >
      {children}
    </motion.div>
  );
}

function ParallaxMedia({ children, className = "", distance = 92 }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [distance, -distance]);
  const scale = useTransform(scrollYProgress, [0, 0.5, 1], [1.08, 1, 1.08]);

  return (
    <div ref={ref} className={`parallax-frame ${className}`}>
      <motion.div className="parallax-frame__inner" style={{ y, scale }}>
        {children}
      </motion.div>
    </div>
  );
}

function ScrollMarquee({ children, reverse = false }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const x = useTransform(scrollYProgress, [0, 1], reverse ? ["8%", "-16%"] : ["-16%", "8%"]);

  return (
    <div ref={ref} className="scroll-marquee" aria-hidden="true">
      <motion.div className="scroll-marquee__track" style={{ x }}>
        <span>{children}</span>
        <span>{children}</span>
      </motion.div>
    </div>
  );
}

// Mirrors backend/design_guide.py's SERVICE_PROVIDER_CUISINE_KEYS — merchant
// types are order-centric (menu + checkout), service_provider types are
// appointment-centric (services + booking flow). Keep these two lists in sync.
const BIZ_TYPES_BY_MODE = {
  merchant: ["restaurant", "bakery", "cafe", "retail", "florist"],
  service_provider: ["salon", "spa", "barber", "clinic", "therapist"],
};

// merchant.ordrs.ai / provider.ordrs.ai are marketing entry points into this
// same builder (not separate deployments — see lib/platformHosts.js, which
// already treats any *.ordrs.ai subdomain as the platform, not a merchant's
// custom storefront). Landing on one just pre-selects the matching toggle
// below; nothing else about the app differs by hostname.
function initialBusinessModeFromHost() {
  if (isProviderHost) return "service_provider";
  return "merchant";
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------
function FieldLabel({ children }) {
  return (
    <label className="text-xs font-bold text-foreground/70 mb-1.5 block">
      {children}
    </label>
  );
}

function SectionHeader({ icon: Icon, label, count }) {
  return (
    <div className="flex items-center gap-2">
      <Icon className="w-3.5 h-3.5 text-primary" />
      <span className="text-xs font-bold text-foreground/70 uppercase tracking-wider">{label}</span>
      {count > 0 && (
        <span className="text-[10px] bg-primary/15 text-primary font-bold px-1.5 py-0.5 rounded-full">
          {count}
        </span>
      )}
    </div>
  );
}

function ColorSection({ primaryColor, setPrimaryColor, secondaryColor, setSecondaryColor }) {
  const [customPrimary, setCustomPrimary] = useState(primaryColor || "");

  const applyPalette = (palette) => {
    setPrimaryColor(palette.primary);
    setSecondaryColor(palette.secondary);
    setCustomPrimary(palette.primary);
  };

  const selectedPaletteId = COLOR_PALETTES.find((p) => p.primary === primaryColor)?.id || null;

  return (
    <div className="space-y-4">
      <div>
        <FieldLabel>Color palette</FieldLabel>
        <div className="grid grid-cols-1 gap-2">
          {COLOR_PALETTES.map((p) => {
            const selected = selectedPaletteId === p.id;
            return (
              <button
                key={p.id}
                onClick={() => applyPalette(p)}
                className={`text-left px-3 py-2.5 rounded-lg border-2 transition-all ${selected ? "border-primary bg-primary/8" : "border-border hover:border-primary/40"}`}
              >
                <div className="flex items-center gap-3">
                  <div className="flex gap-1 shrink-0">
                    {p.swatches.map((hex, i) => (
                      <div
                        key={i}
                        className="w-5 h-5 rounded-sm"
                        style={{ background: hex }}
                      />
                    ))}
                  </div>
                  <div className="min-w-0">
                    <div className="text-xs font-semibold leading-tight">{p.name}</div>
                    <div className="text-[10px] text-muted-foreground leading-tight mt-0.5">{p.desc}</div>
                  </div>
                </div>
              </button>
            );
          })}
        </div>
        <div className="flex items-center gap-2 mt-3">
          <div
            className="w-7 h-7 rounded border border-border shrink-0"
            style={{ background: isValidHex(customPrimary) ? customPrimary : "#e5e7eb" }}
          />
          <Input
            value={customPrimary}
            onChange={(e) => {
              setCustomPrimary(e.target.value);
              if (isValidHex(e.target.value)) setPrimaryColor(e.target.value);
            }}
            placeholder="Custom primary hex"
            className="h-8 text-xs font-mono max-w-[160px]"
          />
          <span className="text-[10px] text-muted-foreground">or type any hex</span>
        </div>
      </div>
    </div>
  );
}

function FontSection({ selectedFont, setSelectedFont, fontFile, setFontFile }) {
  const fileRef = useRef();

  return (
    <div className="space-y-4">
      <div>
        <FieldLabel>Font pairing</FieldLabel>
        <div className="grid grid-cols-1 gap-2">
          {FONT_PAIRS.map((fp) => (
            <button
              key={fp.id}
              onClick={() => setSelectedFont(fp.id)}
              className={`text-left px-3 py-2.5 rounded-lg border-2 transition-all ${selectedFont === fp.id ? "border-primary bg-primary/8" : "border-border hover:border-primary/40"}`}
            >
              <div className="flex items-baseline justify-between gap-2 mb-0.5">
                <span
                  className="text-base font-bold leading-none"
                  style={{ fontFamily: `'${fp.heading}', serif` }}
                >
                  {fp.sample}
                </span>
                <span className="text-[10px] text-muted-foreground shrink-0 font-medium">{fp.label}</span>
              </div>
              <div className="text-[10px] text-muted-foreground">
                {fp.heading} · {fp.body}
              </div>
            </button>
          ))}
        </div>
      </div>

      <div>
        <FieldLabel>Upload font file <span className="font-normal text-muted-foreground">(.ttf / .otf / .woff2 — optional)</span></FieldLabel>
        <div className="flex items-center gap-2">
          <button
            onClick={() => fileRef.current?.click()}
            className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded border border-border hover:border-primary/50 transition-colors text-muted-foreground hover:text-foreground"
          >
            <Upload className="w-3 h-3" />
            {fontFile ? fontFile.name : "Choose file"}
          </button>
          {fontFile && (
            <button onClick={() => setFontFile(null)} className="text-muted-foreground hover:text-foreground">
              <X className="w-3.5 h-3.5" />
            </button>
          )}
          <input
            ref={fileRef}
            type="file"
            accept=".ttf,.otf,.woff,.woff2"
            className="hidden"
            onChange={(e) => setFontFile(e.target.files?.[0] || null)}
          />
        </div>
        {fontFile && (
          <p className="text-[10px] text-muted-foreground mt-1">Font name extracted from filename.</p>
        )}
      </div>
    </div>
  );
}

function VibeSection({ vibeTags, setVibeTags, customAesthetics, setCustomAesthetics }) {
  const toggle = (id) =>
    setVibeTags((prev) =>
      prev.includes(id) ? prev.filter((v) => v !== id) : [...prev, id]
    );

  return (
    <div className="space-y-4">
      <div>
        <FieldLabel>Mood / vibe <span className="font-normal text-muted-foreground">(pick any)</span></FieldLabel>
        <div className="flex flex-wrap gap-2">
          {VIBE_TAGS.map((v) => (
            <button
              key={v.id}
              onClick={() => toggle(v.id)}
              className={`text-xs px-3 py-1.5 rounded-full border transition-all ${vibeTags.includes(v.id) ? "border-primary bg-primary/12 text-foreground font-semibold" : "border-border text-muted-foreground hover:border-primary/40 hover:text-foreground"}`}
            >
              {v.label}
            </button>
          ))}
        </div>
      </div>

      <div>
        <div className="flex items-center gap-1 mb-1.5">
          <Wand2 className="w-3.5 h-3.5 text-primary" />
          <FieldLabel>Describe your own aesthetic <span className="font-normal text-muted-foreground">(optional — these vibe directly into the AI)</span></FieldLabel>
        </div>
        <Textarea
          value={customAesthetics}
          onChange={(e) => setCustomAesthetics(e.target.value)}
          rows={3}
          placeholder="e.g. 'Brutalist concrete with bright lime accents. Lowercase everything. Italic serif on prices. Dark and moody like a jazz club.'"
          className="text-sm"
        />
        {vibeTags.length > 0 && (
          <p className="text-[10px] text-muted-foreground mt-1">
            Vibes selected: <span className="text-foreground font-medium">{vibeTags.join(", ")}</span> — will be added to generation prompt.
          </p>
        )}
      </div>
    </div>
  );
}

function LogoSection({ logoPreview, setLogoPreview, setLogoFile, setLogoUrl }) {
  const fileRef = useRef();

  const onFile = useCallback(async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    // Show local preview immediately
    const reader = new FileReader();
    reader.onload = (ev) => setLogoPreview(ev.target.result);
    reader.readAsDataURL(f);
    setLogoFile(f);
  }, [setLogoPreview, setLogoFile]);

  return (
    <div className="space-y-3">
      <FieldLabel>Logo / brand mark <span className="font-normal text-muted-foreground">(optional — PNG, SVG, JPG under 2 MB)</span></FieldLabel>
      <div className="flex items-center gap-3">
        {logoPreview ? (
          <div className="relative">
            <img
              src={logoPreview}
              alt="Logo preview"
              className="w-16 h-16 rounded-lg object-contain border border-border bg-muted/30"
            />
            <button
              onClick={() => { setLogoPreview(null); setLogoFile(null); setLogoUrl(null); }}
              className="absolute -top-1.5 -right-1.5 w-4 h-4 rounded-full bg-background border border-border flex items-center justify-center hover:bg-destructive hover:border-destructive hover:text-white transition-colors"
            >
              <X className="w-2.5 h-2.5" />
            </button>
          </div>
        ) : (
          <button
            onClick={() => fileRef.current?.click()}
            className="w-16 h-16 rounded-lg border-2 border-dashed border-border hover:border-primary/50 flex flex-col items-center justify-center gap-1 transition-colors text-muted-foreground hover:text-foreground"
          >
            <ImageIcon className="w-5 h-5" />
            <span className="text-[9px]">Upload</span>
          </button>
        )}
        <div className="text-xs text-muted-foreground">
          <p>Your logo will be placed in the storefront header.</p>
          <p className="mt-0.5 text-[10px]">No logo? We'll use a styled wordmark from your business name.</p>
        </div>
      </div>
      <input
        ref={fileRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={onFile}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Customize panel — the progressive reveal section
// ---------------------------------------------------------------------------
const CUSTOMIZE_TABS = [
  { id: "style",  label: "Style",  icon: Sparkles },
  { id: "colors", label: "Colors", icon: Palette },
  { id: "fonts",  label: "Fonts",  icon: Type },
  { id: "vibe",   label: "Vibe",   icon: Wand2 },
  { id: "logo",   label: "Logo",   icon: ImageIcon },
];

function customizeCount(tab, state) {
  if (tab === "style")  return (state.style !== "warm_editorial" ? 1 : 0) + (state.cuisineHint ? 1 : 0);
  if (tab === "colors") return (state.primaryColor ? 1 : 0) + (state.secondaryColor ? 1 : 0);
  if (tab === "fonts")  return (state.selectedFont || state.fontFile) ? 1 : 0;
  if (tab === "vibe")   return state.vibeTags.length + (state.customAesthetics.trim() ? 1 : 0);
  if (tab === "logo")   return state.logoPreview ? 1 : 0;
  return 0;
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------
export default function Landing() {
  const nav = useNavigate();
  const heroRef = useRef(null);
  const { scrollYProgress: pageProgress } = useScroll();
  const { scrollYProgress: heroProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"],
  });
  const heroImageY = useTransform(heroProgress, [0, 1], ["0%", "24%"]);
  const heroCopyY = useTransform(heroProgress, [0, 1], ["0%", "-18%"]);
  const heroCopyOpacity = useTransform(heroProgress, [0, 0.82], [1, 0.38]);
  const heroOrbitLeftY = useTransform(heroProgress, [0, 1], [0, -120]);
  const heroOrbitRightY = useTransform(heroProgress, [0, 1], [0, 150]);

  // Basic
  const [name, setName] = useState("");
  // merchant | service_provider — sets the dashboard/storefront structure,
  // locked in at build. Defaults to matching the subdomain the visitor
  // arrived on (merchant.ordrs.ai / provider.ordrs.ai); still user-editable
  // via the toggle below.
  const [businessMode, setBusinessMode] = useState(initialBusinessModeFromHost);
  const [bizType, setBizType] = useState(() => BIZ_TYPES_BY_MODE[initialBusinessModeFromHost()][0]);
  const [address, setAddress] = useState("");
  const [cuisineHint, setCuisineHint] = useState("");
  const [menuText, setMenuText] = useState("");
  const [scrapeUrl, setScrapeUrl] = useState("");
  const [loading, setLoading] = useState(false);
  const [brandPack, setBrandPack] = useState(null);
  const [extracting, setExtracting] = useState(false);
  const [resumeSession, setResumeSession] = useState(null); // { slug, name } from cookie

  useEffect(() => {
    const s = BUILD_SESSION.get();
    if (s?.slug) setResumeSession(s);
  }, []);

  // Customize panel
  const [customizeOpen, setCustomizeOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("style");

  // Style
  const [style, setStyle] = useState("warm_editorial");

  // Colors
  const [primaryColor, setPrimaryColor] = useState("");
  const [secondaryColor, setSecondaryColor] = useState("");

  // Fonts
  const [selectedFont, setSelectedFont] = useState(null);
  const [fontFile, setFontFile] = useState(null);

  // Vibe
  const [vibeTags, setVibeTags] = useState([]);
  const [customAesthetics, setCustomAesthetics] = useState("");

  // Logo
  const [logoPreview, setLogoPreview] = useState(null);
  const [logoFile, setLogoFile] = useState(null);
  const [logoUrl, setLogoUrl] = useState(null);

  const customizeState = { style, cuisineHint, primaryColor, secondaryColor, selectedFont, fontFile, vibeTags, customAesthetics, logoPreview };
  const totalCustomized = CUSTOMIZE_TABS.reduce((n, t) => n + customizeCount(t.id, customizeState), 0);

  const extractBrand = async () => {
    if (!scrapeUrl.trim()) return;
    setExtracting(true);
    try {
      const r = await api.post("/extract-brand-pack", { url: scrapeUrl });
      setBrandPack(r.data);
      if (r.data.hero_image || r.data.primary_color) toast.success("Brand pack found");
    } catch { /* silent */ }
    finally { setExtracting(false); }
  };

  const submit = async () => {
    if (!name.trim()) return toast.error("Business name required");
    if (!scrapeUrl.trim())
      return toast.error("Your website URL is required so we can parse your menu/services and branding.");
    if (!scrapeUrl.includes("."))
      return toast.error("Enter your website URL — e.g. https://yourbusiness.com");
    setLoading(true);
    try {
      // Upload logo first if provided and not yet uploaded
      let finalLogoUrl = logoUrl;
      if (logoFile && !finalLogoUrl) {
        try {
          const fd = new FormData();
          fd.append("file", logoFile);
          const lr = await api.post("/upload-logo", fd, { headers: { "Content-Type": "multipart/form-data" } });
          finalLogoUrl = lr.data.url;
        } catch { /* logo upload failure is non-fatal */ }
      }

      // Resolve font name from uploaded file, if any
      const resolvedFontName = fontFile
        ? fontFile.name.replace(/\.[^.]+$/, "").replace(/[-_]/g, " ")
        : null;

      const res = await api.post("/instant-preview", {
        business_name: name,
        business_type: bizType,
        business_category: businessMode,
        site_mode: "existing_site",
        brand_style: style,
        custom_aesthetics: customAesthetics || null,
        address: address || null,
        cuisine_hint: cuisineHint || null,
        raw_menu_text: menuText || null,
        scrape_url: scrapeUrl || null,
        primary_color: isValidHex(primaryColor) ? primaryColor : null,
        secondary_color: isValidHex(secondaryColor) ? secondaryColor : null,
        custom_font_name: resolvedFontName || null,
        logo_url: finalLogoUrl || null,
        vibe_tags: vibeTags.length > 0 ? vibeTags : null,
      });
      BUILD_SESSION.set(res.data.slug, name.trim(), res.data.capability);

      nav(`/preview/${res.data.slug}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to build preview");
    } finally { setLoading(false); }
  };

  return (
    <div className="landing-scroll">
      <motion.div className="scroll-progress" style={{ scaleX: pageProgress }} />
      <RevealNav />

      {/* Resume-build banner — shown only when a prior build session cookie exists */}
      {resumeSession && (
        <div style={{
          position: "fixed", top: 0, left: 0, right: 0, zIndex: 50,
          background: "#1a1a1a", color: "#f5f5f5",
          borderBottom: "1px solid #333",
          display: "flex", alignItems: "center", justifyContent: "center",
          gap: 16, padding: "10px 20px", fontSize: 13,
        }}>
          <span>
            Welcome back — <strong>{resumeSession.name}</strong> is still here.
          </span>
          <a
            href={`/build/${resumeSession.slug}`}
            style={{
              background: "var(--color-primary, #d4724a)", color: "#fff",
              padding: "5px 14px", borderRadius: 6, fontWeight: 600,
              textDecoration: "none", fontSize: 12, whiteSpace: "nowrap",
            }}
          >
            Resume build →
          </a>
          <span style={{ color: "#888" }}>·</span>
          <a
            href="/contact"
            style={{ color: "#aaa", fontSize: 12, textDecoration: "none", whiteSpace: "nowrap" }}
          >
            Something feel off? Contact support
          </a>
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              BUILD_SESSION.clear();
              setResumeSession(null);
            }}
            style={{
              marginLeft: "auto", background: "none", border: "none",
              color: "#666", cursor: "pointer", fontSize: 18, lineHeight: 1, padding: "0 4px",
            }}
            aria-label="Dismiss"
          >×</button>
        </div>
      )}

      {/* HERO */}
      <section ref={heroRef} className="landing-hero relative overflow-hidden">
        <div className="absolute inset-0 -z-10">
          <motion.img
            src="https://images.pexels.com/photos/29394552/pexels-photo-29394552.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1600"
            alt=""
            className="w-full h-[118%] object-cover opacity-20"
            style={{ y: heroImageY }}
          />
          <div className="absolute inset-0 bg-gradient-to-b from-background via-background/80 to-background" />
        </div>
        <motion.div
          className="hero-depth-plane hero-depth-plane--left"
          style={{ y: heroOrbitLeftY }}
          aria-hidden="true"
        />
        <motion.div
          className="hero-depth-plane hero-depth-plane--right"
          style={{ y: heroOrbitRightY }}
          aria-hidden="true"
        />
        <div className="ds-container pt-24 pb-20 sm:pt-32 sm:pb-24 lg:pt-40 lg:pb-32 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            style={{ y: heroCopyY, opacity: heroCopyOpacity }}
          >
            <p className="overline mb-7">
              Multi-channel order process · Custom web · backend portal · ai
              phone
            </p>
            <h1 className="text-5xl sm:text-6xl lg:text-7xl mb-6">
              Your business,
              <br />
              <span className="font-script text-primary text-6xl sm:text-7xl lg:text-[7.5rem] font-normal leading-[0.7] block mt-3">
                ready to take ordrs
              </span>
              <span className="block mt-3">in 5 minutes.</span>
            </h1>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto mb-8 leading-relaxed">
              Get a full custom website, an AI phone host that never sleeps, and direct-to-backend system that processes orders and your books.
            </p>
            <div className="flex flex-wrap justify-center items-center gap-5 text-sm text-muted-foreground">
              <span className="flex items-center gap-1.5">
                <Zap className="w-4 h-4 text-primary" />
                LIVE DEPLOYMENT
              </span>
              <span className="flex items-center gap-1.5">
                <Phone className="w-4 h-4 text-primary" />
                NEW PHONE NUMBER
              </span>
              <span className="flex items-center gap-1.5">
                <ShieldCheck className="w-4 h-4 text-primary" />
                ESTABLISHMENT
              </span>
              <span className="flex items-center gap-1.5">
                <Building2 className="w-4 h-4 text-primary" />
                ENTERPRISE
              </span>
            </div>
          </motion.div>
          <motion.a
            href="#preview"
            className="scroll-cue"
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.75 }}
          >
            <span>scroll</span>
            <i />
            <span>to build</span>
          </motion.a>
        </div>
      </section>

      {/* BUILD BOX */}
      <ScrollSection id="preview" className="ds-container pb-36 lg:pb-48 pt-12 lg:pt-20">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2, duration: 0.6 }}
        >
          <Card className="tracing-beam p-8 lg:p-12 bg-card border-0 shadow-2xl">
            {/* Header */}
            <div className="flex items-center justify-between mb-1 flex-wrap gap-2">
              <div className="flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-primary" />
                <span className="overline">Instant preview</span>
              </div>
              <span className="text-xs text-muted-foreground">
                FREE PARSING · NO CARD OR ACCOUNT REQUIRED
              </span>
            </div>
            <h3 className="text-3xl lg:text-4xl mb-1">
              See your storefront.{" "}
              <span className="font-script text-primary text-5xl lg:text-6xl font-normal">
                Live.
              </span>
            </h3>
            <p className="text-sm text-muted-foreground mb-8">
              The more you tell us, the more on-brand the result.
            </p>

            {/* ── Two-col: Identity (left) + Menu source (right) ── */}
            <div className="grid lg:grid-cols-2 gap-8 mb-6">
              {/* LEFT — who you are */}
              <div className="space-y-4">
                <div>
                  <FieldLabel>Business name *</FieldLabel>
                  <Input
                    data-testid="preview-name-input"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Osteria della Luna"
                    className="h-11"
                  />
                </div>
                <div>
                  <FieldLabel>
                    Address{" "}
                    <span className="font-normal text-muted-foreground">
                      (optional)
                    </span>
                  </FieldLabel>
                  <Input
                    value={address}
                    onChange={(e) => setAddress(e.target.value)}
                    placeholder="123 Market St, Brooklyn"
                  />
                </div>
                <div>
                  <FieldLabel>What kind of business?</FieldLabel>
                  <div className="grid grid-cols-2 gap-2 mt-1">
                    {[
                      { id: "merchant", label: "I TAKE ORDERS", hint: "Restaurants, bakeries, florists, retail shops..." },
                      { id: "service_provider", label: "I TAKE APPOINTMENTS", hint: "Salons, clinics, therapists, wellness studios..." },
                    ].map((m) => (
                      <button
                        key={m.id}
                        type="button"
                        data-testid={`business-mode-${m.id}`}
                        onClick={() => {
                          setBusinessMode(m.id);
                          setBizType(BIZ_TYPES_BY_MODE[m.id][0]);
                        }}
                        className={`text-left px-3 py-2.5 rounded-lg border transition-colors ${
                          businessMode === m.id
                            ? "bg-primary text-primary-foreground border-primary"
                            : "bg-transparent border-border hover:border-primary/50"
                        }`}
                      >
                        <div className="text-sm font-bold">{m.label}</div>
                        <div className={`text-[11px] mt-0.5 ${businessMode === m.id ? "text-primary-foreground/80" : "text-muted-foreground"}`}>{m.hint}</div>
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* RIGHT — website source first, optional menu backup below */}
              <div className="space-y-4">
                <div>
                  <FieldLabel>
                    <span className="flex items-center gap-1">
                      <Globe className="w-3.5 h-3.5" />
                      Your website *
                    </span>
                  </FieldLabel>
                  <Input
                    data-testid="preview-scrape-url"
                    value={scrapeUrl}
                    onChange={(e) => setScrapeUrl(e.target.value)}
                    onBlur={extractBrand}
                    placeholder="https://your-current-site.com"
                  />
                  <p className="text-[10px] text-muted-foreground mt-1">
                    {extracting
                      ? "Extracting brand pack…"
                      : brandPack &&
                          (brandPack.hero_image || brandPack.primary_color)
                        ? "✓ Brand colors + menu auto-extracted"
                        : "We'll scan this for menu/services and branding, then create your templated Ordrs ordering page from it."}
                  </p>
                  {brandPack &&
                    (brandPack.hero_image || brandPack.primary_color) && (
                      <div className="mt-2 flex items-center gap-2 p-2 rounded-lg border border-border bg-muted/40">
                        {brandPack.hero_image && (
                          <img
                            src={brandPack.hero_image}
                            alt=""
                            className="w-10 h-10 rounded object-cover shrink-0"
                          />
                        )}
                        {brandPack.primary_color && (
                          <div
                            className="w-6 h-6 rounded border shrink-0"
                            style={{ background: brandPack.primary_color }}
                            title={brandPack.primary_color}
                          />
                        )}
                        {brandPack.font_family && (
                          <span className="text-[10px] text-muted-foreground truncate">
                            {brandPack.font_family}
                          </span>
                        )}
                      </div>
                    )}
                </div>

                <div>
                  <FieldLabel>
                    {businessMode === "service_provider" ? "Paste services & prices" : "Paste menu text"}{" "}
                    <span className="font-normal text-muted-foreground">
                      (optional backup)
                    </span>
                  </FieldLabel>
                  <Textarea
                    data-testid="preview-menu-text"
                    value={menuText}
                    onChange={(e) => setMenuText(e.target.value)}
                    rows={3}
                    placeholder={
                      businessMode === "service_provider"
                        ? "Haircut 65, Color 175, Blowout 45\nManicure 35, Pedicure 50..."
                        : "Margherita 18, Calamari 14, Tiramisu 9\nBurrata 16, Tagliatelle 24..."
                    }
                    className="text-sm resize-none"
                  />
                </div>
              </div>
            </div>

            {/* ── Progressive reveal: Customize further ── */}
            <div className="border border-border rounded-xl overflow-hidden mb-6">
              <button
                onClick={() => setCustomizeOpen((o) => !o)}
                className="w-full flex items-center justify-between px-5 py-3.5 hover:bg-muted/40 transition-colors"
              >
                <div className="flex items-center gap-2">
                  <Palette className="w-4 h-4 text-primary" />
                  <span className="text-sm font-semibold">
                    Customize colors, fonts &amp; vibe
                  </span>
                  {totalCustomized > 0 && (
                    <span className="text-[10px] bg-primary text-primary-foreground font-bold px-2 py-0.5 rounded-full">
                      {totalCustomized} set
                    </span>
                  )}
                </div>
                <ChevronDown
                  className={`w-4 h-4 text-muted-foreground transition-transform duration-200 ${customizeOpen ? "rotate-180" : ""}`}
                />
              </button>

              <AnimatePresence initial={false}>
                {customizeOpen && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.25, ease: "easeInOut" }}
                    className="overflow-hidden"
                  >
                    <div className="border-t border-border">
                      {/* Tab bar — scrollable on mobile */}
                      <div className="flex overflow-x-auto border-b border-border bg-muted/20 scrollbar-none" style={{ scrollbarWidth: "none" }}>
                        {CUSTOMIZE_TABS.map((t) => {
                          const count = customizeCount(t.id, customizeState);
                          const Icon = t.icon;
                          return (
                            <button
                              key={t.id}
                              onClick={() => setActiveTab(t.id)}
                              className={`flex shrink-0 items-center gap-1.5 px-4 py-2.5 text-xs font-medium border-b-2 transition-all ${activeTab === t.id ? "border-primary text-foreground" : "border-transparent text-muted-foreground hover:text-foreground"}`}
                            >
                              <Icon className="w-3.5 h-3.5" />
                              {t.label}
                              {count > 0 && (
                                <span className="w-4 h-4 rounded-full bg-primary/20 text-primary text-[9px] font-bold flex items-center justify-center">
                                  {count}
                                </span>
                              )}
                            </button>
                          );
                        })}
                      </div>

                      {/* Tab panels */}
                      <div className="p-5">
                        {activeTab === "style" && (
                          <div className="space-y-5">
                            <div>
                              <p className="text-xs text-muted-foreground mb-3">
                                The archetype sets the structural personality —
                                template, layout density, type hierarchy. Colors
                                and fonts override the defaults.
                              </p>
                              <div className="grid grid-cols-2 gap-2">
                                {STYLE_CHIPS.map((s) => (
                                  <button
                                    key={s.id}
                                    data-testid={`style-chip-${s.id}`}
                                    onClick={() => setStyle(s.id)}
                                    className={`text-left text-xs py-3 px-4 rounded-lg border-2 transition-all ${style === s.id ? "border-primary bg-primary/10" : "border-border hover:border-primary/50"}`}
                                  >
                                    <div className="font-bold mb-0.5">
                                      {s.label}
                                    </div>
                                    <div className="text-muted-foreground text-[10px]">
                                      {s.desc}
                                    </div>
                                  </button>
                                ))}
                              </div>
                            </div>
                            <div>
                              <FieldLabel>
                                {businessMode === "service_provider" ? "Category" : "Cuisine / category"}{" "}
                                <span className="font-normal text-muted-foreground">
                                  (optional — improves image matching)
                                </span>
                              </FieldLabel>
                              <div className="flex flex-wrap gap-1.5">
                                {(businessMode === "service_provider" ? SERVICE_CATEGORY_TAGS : CUISINE_TAGS).map((c) => (
                                  <button
                                    key={c}
                                    onClick={() =>
                                      setCuisineHint(cuisineHint === c ? "" : c)
                                    }
                                    className={`text-[11px] px-2.5 py-1 rounded-full border transition-all ${cuisineHint === c ? "border-primary bg-primary/12 text-foreground font-semibold" : "border-border text-muted-foreground hover:border-primary/40"}`}
                                  >
                                    {c}
                                  </button>
                                ))}
                              </div>
                            </div>
                          </div>
                        )}
                        {activeTab === "colors" && (
                          <ColorSection
                            primaryColor={primaryColor}
                            setPrimaryColor={setPrimaryColor}
                            secondaryColor={secondaryColor}
                            setSecondaryColor={setSecondaryColor}
                          />
                        )}
                        {activeTab === "fonts" && (
                          <FontSection
                            selectedFont={selectedFont}
                            setSelectedFont={setSelectedFont}
                            fontFile={fontFile}
                            setFontFile={setFontFile}
                          />
                        )}
                        {activeTab === "vibe" && (
                          <VibeSection
                            vibeTags={vibeTags}
                            setVibeTags={setVibeTags}
                            customAesthetics={customAesthetics}
                            setCustomAesthetics={setCustomAesthetics}
                          />
                        )}
                        {activeTab === "logo" && (
                          <LogoSection
                            logoPreview={logoPreview}
                            setLogoPreview={setLogoPreview}
                            setLogoFile={setLogoFile}
                            setLogoUrl={setLogoUrl}
                          />
                        )}
                      </div>

                      {/* What the AI sees */}
                      {(vibeTags.length > 0 ||
                        customAesthetics.trim() ||
                        primaryColor ||
                        secondaryColor ||
                        cuisineHint) && (
                        <div className="mx-5 mb-5 p-3 rounded-lg bg-muted/50 border border-border">
                          <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider mb-1.5">
                            What the AI sees →
                          </p>
                          <p className="text-xs text-foreground/80 font-mono leading-relaxed">
                            {[
                              cuisineHint && `Cuisine: ${cuisineHint}`,
                              primaryColor && `Primary color: ${primaryColor}`,
                              secondaryColor &&
                                `Secondary color: ${secondaryColor}`,
                              vibeTags.length > 0 &&
                                `Vibe: ${vibeTags.join(", ")}`,
                              customAesthetics.trim() &&
                                customAesthetics.trim(),
                            ]
                              .filter(Boolean)
                              .join(". ")}
                          </p>
                        </div>
                      )}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* ── Submit ── */}
            <Button
              data-testid="preview-submit-btn"
              onClick={submit}
              disabled={loading}
              className="w-full h-14 text-lg font-bold group"
            >
              {loading ? (
                "Building your storefront..."
              ) : (
                <>
                  Build my storefront{" "}
                  <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
                </>
              )}
            </Button>
            <p className="text-[11px] text-muted-foreground text-center mt-3">
              You'll have the option to iterate on the result before live deployment.
            </p>
          </Card>
        </motion.div>
      </ScrollSection>

      {/* MERCHANT VOICE SAMPLE */}
      <ScrollSection className="ds-container py-24 lg:py-32">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">
          <div className="lg:col-span-5">
            <p className="overline mb-4">the merchant voice</p>
            <h2 className="text-3xl lg:text-5xl mb-5">
              Hear what your customers hear.
            </h2>
            <p className="text-lg text-muted-foreground leading-relaxed">
              The phone host is part of the operating system, not a separate demo. Listen to a sample before you build your storefront.
            </p>
          </div>
          <div className="lg:col-span-7">
            <VoiceSamplePlayer
              src="/merchant.wav"
              title="Merchant phone host sample"
              description="Stream the generated merchant voice sample directly in your browser without a download button. Live calls can use streaming TTS for lower-latency responses."
              testId="merchant-voice-sample"
            />
          </div>
        </div>
      </ScrollSection>

      {/* THE COGNITION */}
      <ScrollMarquee>AI phone host · live transcripts · always ready ·</ScrollMarquee>
      <ScrollSection className="ds-container py-40 lg:py-56">
        <p className="overline mb-4">the cognition</p>
        <h2 className="text-4xl lg:text-5xl mb-4">
          Next-gen AI
          <br />
          <span className="font-script text-primary text-6xl lg:text-7xl font-normal leading-[0.7] block mt-3">
            phone receptionist.
          </span>
          <span className="block mt-3">Always ready.</span>
        </h2>
        <Card className="overflow-hidden mt-10">
          <div className="grid lg:grid-cols-2">
            {/* Video */}
            <div className="bg-foreground">
              <ParallaxMedia>
                <video
                  src="/AI_phone_host_taking_order.mp4"
                  autoPlay
                  muted
                  loop
                  playsInline
                  controls
                  className="w-full h-full object-cover"
                />
              </ParallaxMedia>
            </div>
            {/* Copy */}
            <div className="p-10 bg-foreground text-background flex flex-col justify-center">
              <Brain className="w-6 h-6 text-primary mb-4" />
              <p className="overline text-background/60 mb-3">call records</p>
              <h3 className="text-2xl mb-4 text-background">
                Hear every word. Miss nothing.
              </h3>
              <p className="text-background/70 leading-relaxed mb-4">
                The AI host fields real orders around the clock. Every caller
                gets a natural conversation — menu questions, modifications,
                upsells — with a full transcript record available in your
                dashboard.
              </p>
              <p className="text-background/60 text-sm leading-relaxed mb-6">
                Every call is logged. From your portal you can review
                full transcripts, see exactly what your host said, adjust accordingly, and know your
                business is covered around the clock — nothing happens off the
                record.
              </p>
              <div className="grid grid-cols-3 gap-3 text-sm text-background/80">
                <div>
                  <div className="text-2xl font-extrabold text-background">8</div>
                  languages
                </div>
                <div>
                  <div className="text-2xl font-extrabold text-background">&lt;800ms</div>
                  response
                </div>
                <div>
                  <div className="text-2xl font-extrabold text-background">24/7</div>
                  uptime
                </div>
              </div>
            </div>
          </div>
        </Card>
      </ScrollSection>

      {/* THE INFRASTRUCTURE */}
      <ScrollSection className="bg-foreground text-background landing-dark-band" tone="dark">
        <div className="ds-container py-36 lg:py-48">
          <p className="overline mb-4 text-primary">the infrastructure</p>
          <h2 className="text-4xl lg:text-5xl mb-4 text-background">
            Enterprise-grade reliability.
            <br />
            <span className="font-script text-primary text-6xl lg:text-7xl font-normal leading-[0.7] block mt-3">
              Total security.
            </span>
          </h2>
          <p className="text-background/70 max-w-2xl leading-relaxed mb-10">
            Your business data belongs to you, period. Built on locked-down,
            corporate-grade database architecture so your system never goes down
            when the kitchen gets slammed.
          </p>
          <div className="grid md:grid-cols-2 gap-4">
            <ScrollCard>
              <Card className="p-8 bg-zinc-900 text-background border-zinc-800 scroll-tilt-card">
                <Lock className="w-5 h-5 text-primary mb-4" />
                <h3 className="text-xl mb-2 text-background">
                  Bulletproof order tracking
                </h3>
                <p className="text-sm text-background/70">
                  Every menu change, custom modification, and ticket is
                  tracked live so orders are never dropped or duplicated.
                </p>
              </Card>
            </ScrollCard>
            <ScrollCard delay={0.12}>
              <Card className="p-8 bg-zinc-900 text-background border-zinc-800 scroll-tilt-card">
                <ShieldCheck className="w-5 h-5 text-primary mb-4" />
                <h3 className="text-xl mb-2 text-background">
                  Bank-level compliance
                </h3>
                <p className="text-sm text-background/70">
                  Built to meet strict corporate security standards, keeping your
                  sales data, guest info, and payouts completely safe.
                </p>
              </Card>
            </ScrollCard>
          </div>
        </div>
      </ScrollSection>

      {/* THE PLATFORM */}
      <ScrollMarquee reverse>zero commissions · direct orders · instant payouts ·</ScrollMarquee>
      <ScrollSection className="ds-container py-40 lg:py-56">
        <p className="overline mb-4">the platform</p>
        <h2 className="text-4xl lg:text-5xl mb-4">
          Direct-to-consumer ordering.
          <br />
          <span className="font-script text-primary text-6xl lg:text-7xl font-normal leading-[0.7] block mt-3">
            Zero commissions.
          </span>
        </h2>
        <p className="text-muted-foreground max-w-2xl leading-relaxed mb-10">
          Stop giving away 30% of your margins to third-party delivery apps.
          ordrs hands control back to your business with a premium checkout
          experience that turns first-time web traffic into loyal, direct
          customers.
        </p>
        <div className="grid md:grid-cols-2 gap-4 mb-10">
          <ScrollCard>
            <Card className="p-8 scroll-tilt-card">
              <h3 className="text-xl mb-2">Own your customer data</h3>
              <p className="text-sm text-muted-foreground">
                Build an automated marketing list with every order. Offer optional loyalty program and SMS capabilities. No
                gatekeepers, no hidden fees.
              </p>
            </Card>
          </ScrollCard>
          <ScrollCard delay={0.12}>
            <Card className="p-8 scroll-tilt-card">
              <h3 className="text-xl mb-2">Instant payouts</h3>
              <p className="text-sm text-muted-foreground">
                Funds route directly to your bank account via secure Stripe
                architecture the second a customer checks out.
              </p>
            </Card>
          </ScrollCard>
        </div>

        <div className="editorial-divider my-16" />

        <p className="overline mb-4">for merchants</p>
        <h2 className="text-4xl lg:text-5xl mb-4">
          Bulletproof order management.
          <br />
          <span className="font-script text-primary text-6xl lg:text-7xl font-normal leading-[0.7] block mt-3">
            No dropped tickets.
          </span>
        </h2>
        <Card className="overflow-hidden mt-10">
          <div className="grid lg:grid-cols-2">
            {/* Copy */}
            <div className="p-10 flex flex-col justify-center">
              <Printer className="w-6 h-6 text-primary mb-4" />
              <p className="overline mb-3">fulfillment queue</p>
              <h3 className="text-2xl mb-4">One queue. Every channel.</h3>
              <p className="text-muted-foreground leading-relaxed mb-4">
                Phone orders, web orders, walk-ins — they all land in a single fulfillment queue the moment payment clears. Your business sees everything in priority order, tickets print automatically, and nothing slips through the cracks.
              </p>
              <p className="text-muted-foreground leading-relaxed">
                Idempotent by design — no duplicates, no re-prints, no chaos during peak hours.
              </p>
            </div>
            {/* Video */}
            <div className="bg-muted">
              <ParallaxMedia distance={76}>
                <video
                  src="/order_flow.mp4"
                  autoPlay
                  muted
                  loop
                  playsInline
                  controls
                  className="w-full h-full object-cover"
                />
              </ParallaxMedia>
            </div>
          </div>
        </Card>
      </ScrollSection>

      {/* FOR SERVICE PROVIDERS */}
      <ScrollSection className="ds-container py-40 lg:py-56">
        <div className="relative z-20 mb-14 max-w-5xl">
          <p className="overline mb-4">for service providers</p>
          <h2 className="text-4xl lg:text-5xl mb-4">
            AI that books appointments.
            <br />
            <span className="font-script text-primary text-5xl sm:text-6xl lg:text-[3.9rem] font-normal leading-[0.82] block mt-1">
              Here for you so you can be there for clients.
            </span>
          </h2>
        </div>
        <Card className="overflow-hidden service-provider-card">
          <div className="grid lg:grid-cols-[minmax(0,1.05fr)_minmax(420px,0.95fr)]">
            {/* Image */}
            <div className="order-2 lg:order-1 service-provider-media">
              <motion.div
                className="service-provider-image-shell"
                initial={{ opacity: 0, y: 52, rotate: -1.5 }}
                whileInView={{ opacity: 1, y: 0, rotate: 0 }}
                viewport={{ once: false, amount: 0.35 }}
                transition={{ duration: 0.85, ease: [0.16, 1, 0.3, 1] }}
              >
                <img
                  src="/appointments.png"
                  alt="Appointment booking calendar"
                  className="service-provider-image"
                />
              </motion.div>
            </div>
            {/* Copy */}
            <div className="p-8 sm:p-10 lg:p-14 flex flex-col justify-center order-1 lg:order-2 relative z-10">
              <CalendarDays className="w-6 h-6 text-primary mb-4" />
              <p className="overline mb-3">Calendar</p>
              <h3 className="text-2xl mb-4">Never miss a booking.</h3>
              <p className="text-muted-foreground leading-relaxed mb-4">
                Your AI receptionist answers calls even after hours, checks real-time availability, answers FAQs, confirms appointments, and sends reminders. Every booking syncs to your dashboard. You see your schedule, customers get confirmation messages, and your books stay accurate.
              </p>
              <p className="text-muted-foreground leading-relaxed">
                Salons, clinics, studios, funeral homes, and any service business that takes appointments — Get an instant business with a custom website, calendar, client management, a new phone number, personalized receptionist, all on one platform.
              </p>
            </div>
          </div>
        </Card>
      </ScrollSection>

      {/* AI ADVISOR */}
      <ScrollMarquee>dashboard coach · contextual tips · live business data ·</ScrollMarquee>
      <ScrollSection className="ds-container py-40 lg:py-56">
        <div className="relative z-20 -mb-6 max-w-4xl">
          <p className="overline mb-4">ai business advisor</p>
          <h2 className="text-4xl lg:text-5xl mb-4">
            Your dashboard comes with a
            <br />
            <span className="font-script text-primary text-5xl sm:text-6xl lg:text-[3.9rem] font-normal leading-[0.82] block mt-1">
              built-in business coach.
            </span>
          </h2>
        </div>
        <div className="advisor-horizontal-strip mt-10">
          <ScrollCard className="h-full" delay={0}>
            <Card className="p-8 flex flex-col scroll-tilt-card">
              <Brain className="w-6 h-6 text-primary mb-4" />
              <h3 className="font-bold text-lg mb-2">Watches and learns</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">
                The AI advisor lives in the footer of your dashboard. It quietly observes what you are working on — orders, appointments, products, services — and offers short, contextual tips based on what is happening right now.
              </p>
            </Card>
          </ScrollCard>
          <ScrollCard className="h-full" delay={0.12}>
            <Card className="p-8 flex flex-col scroll-tilt-card">
              <Database className="w-6 h-6 text-primary mb-4" />
              <h3 className="font-bold text-lg mb-2">Knows your business</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">
                It pulls live data from your storefront — your business name, pending orders, top sellers, appointment volume, catalog gaps. Every suggestion is specific to your actual data, not generic advice.
              </p>
            </Card>
          </ScrollCard>
          <ScrollCard className="h-full" delay={0.24}>
            <Card className="p-8 flex flex-col scroll-tilt-card">
              <Zap className="w-6 h-6 text-primary mb-4" />
              <h3 className="font-bold text-lg mb-2">Always on, never in the way</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">
                Tips rotate in a subtle bottom bar. Click to pause. No popups, no interruptions. It is like having a silent business partner who only speaks when they have something worth saying.
              </p>
            </Card>
          </ScrollCard>
        </div>
        <div className="mt-8 text-center">
          <p className="text-sm text-muted-foreground">
            <strong className="text-foreground">Included with every plan.</strong> No setup. No configuration. It activates the moment you open your dashboard.
          </p>
        </div>
      </ScrollSection>

      {/* CTA */}
      <ScrollSection className="border-t border-border cta-scroll-section">
        <div className="ds-container py-32 lg:py-44 text-center">
          <h2 className="text-4xl lg:text-5xl mb-4">
            <span className="font-script text-primary text-5xl lg:text-6xl font-normal block">
              Your storefront.
            </span>
            Your AI phone host. Your orders — all in one place.
          </h2>
          <p className="text-muted-foreground mb-6 max-w-xl mx-auto">
            Generate your free preview. No card required. Deploy in 5 minutes.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <a href="#preview">
              <Button
                size="lg"
                className="h-12 px-8 text-base font-bold"
                data-testid="cta-build"
              >
                Build your storefront — free
              </Button>
            </a>
            <Link to="/demo">
              <Button
                size="lg"
                variant="outline"
                className="h-12 px-8 text-base font-bold"
                data-testid="cta-demo"
              >
                See a live demo →
              </Button>
            </Link>
          </div>
        </div>
      </ScrollSection>

      <SiteFooter />
    </div>
  );
}
